import { useRef, useState } from "react";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { AutoScroller } from "@dnd-kit/dom";
import { move } from "@dnd-kit/helpers";
import { TASK_AUTO_SCROLL, pointer } from "./TaskGroupViews";

// AŞAMA 12 — İş kolu (görev kolu) ağacının dnd-kit ile sıralanması ve
// sürüklenerek Neural Link panelinin DIŞINA alınması (Adım 1 POC'u Adım 3'te
// kalıcı hale getirildi; bayrak kaldırıldı, native HTML5 yol temizlendi).

// Sürükleme, Neural Link panelinin (görevler paneli kökü) DIŞINA bırakıldı mı?
// Sınır sabit değer değil: panelin kendi kutusu. Bulunamazsa asla "dışarıda"
// demeyiz (yanlışlıkla pencere açılmasın).
const panelBoundsOf = (node) => node?.closest?.('[data-testid="tasks-panel"]') || null;
export const isOutsideCatPanel = (node, x, y) => {
  const el = panelBoundsOf(node);
  if (!el || x == null || y == null) return false;
  const r = el.getBoundingClientRect();
  return x < r.left || x > r.right || y < r.top || y > r.bottom;
};

// Görev sağlayıcısından BAĞIMSIZ, yalnızca `type: "category"` için sağlayıcı.
// Kaydırma ayarı görevlerle AYNI (acceleration 8, threshold {x:0, y:0.2}).
// Sıra `move()` ile canlı tutulur; bırakıldığında:
//  · panel İÇİNDE  → onCatReorder(yeniIdDizisi, kaynakId)  (kardeş sıralama)
//  · panel DIŞINDA → onCatDropOutside(kaynakId, {x, y})     (yüzen pencere)
export const CategoryDndProvider = ({ ids, onCatReorder, onCatDropOutside, children }) => {
  const rootRef = useRef(null);
  const previewRef = useRef(ids);
  const [outside, setOutside] = useState(false);
  previewRef.current = previewRef.current.length === ids.length ? previewRef.current : ids;
  const disaridaMi = () => isOutsideCatPanel(rootRef.current, pointer.x, pointer.y);
  return (
    <DragDropProvider
      plugins={(defaults) => [...defaults.filter((p) => p !== AutoScroller), TASK_AUTO_SCROLL]}
      onDragStart={() => { previewRef.current = ids; }}
      onDragMove={() => {
        if (!onCatDropOutside) return;
        const out = disaridaMi();
        setOutside((prev) => (prev === out ? prev : out));
      }}
      onDragOver={(event) => {
        previewRef.current = move(previewRef.current, event);
        if (onCatDropOutside) {
          const out = disaridaMi();
          setOutside((prev) => (prev === out ? prev : out));
        }
      }}
      onDragEnd={(event) => {
        const sourceId = event?.operation?.source?.id;
        const out = disaridaMi();
        setOutside(false);
        if (event?.canceled || !sourceId) { previewRef.current = ids; return; }
        if (onCatDropOutside && out) {
          previewRef.current = ids;
          onCatDropOutside(sourceId, { x: pointer.x, y: pointer.y });
          return;
        }
        onCatReorder?.(previewRef.current, sourceId);
      }}
    >
      <div
        ref={rootRef}
        data-testid="category-tree-root"
        data-cat-outside-drop={outside ? "true" : "false"}
        className={outside ? "rounded-lg ring-1 ring-sertex-cyan/60" : ""}
      >
        {children}
      </div>
    </DragDropProvider>
  );
};

// Sıralanabilir kol satırı. `ref` DIŞ sarmalayıcıya bağlanır; `data-cat-drop`
// taşıyan satır İÇ eleman olarak kalır (görev → iş kolu atama zinciri
// `elementsFromPoint` ile bozulmadan çalışsın). `children` bir fonksiyondur:
// tutamaç ref'ini alır (sürükleme YALNIZCA tutamaçtan başlar).
export const SortableCategoryRow = ({ id, index, children }) => {
  const { ref, handleRef, isDragSource } = useSortable({
    id,
    index,
    type: "category",
    accept: ["category"],
  });
  return (
    <div ref={ref} data-poc-cat-row={id} data-dragsource={isDragSource ? "true" : "false"}>
      {children(handleRef, isDragSource)}
    </div>
  );
};
